import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Shield, KeyRound, Loader2 } from "lucide-react";

export default function Settings() {
  const { user, refresh } = useAuth();
  const [qr, setQr] = useState(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/payments/portal", {
        return_url: window.location.origin + "/settings",
      });
      window.location.href = data.portal_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open billing portal");
      setPortalLoading(false);
    }
  };

  const startSetup = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setQr(data);
    } catch (e) {
      toast.error("Could not start 2FA setup");
    } finally {
      setLoading(false);
    }
  };

  const verify = async () => {
    try {
      await api.post("/auth/2fa/verify", { code });
      toast.success("2FA enabled");
      setQr(null);
      setCode("");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid code");
    }
  };

  const disable = async () => {
    await api.post("/auth/2fa/disable");
    toast.success("2FA disabled");
    refresh();
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10 max-w-3xl">
        <div className="vq-overline mb-2">Account</div>
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900 mb-10">
          Settings
        </h1>

        {/* Profile */}
        <div className="vq-card p-6 mb-6">
          <h2 className="font-display font-bold text-lg text-slate-900 mb-4">Profile</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Name</Label>
              <div className="font-semibold text-slate-900 mt-1">{user?.name}</div>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Email</Label>
              <div className="font-semibold text-slate-900 mt-1">{user?.email}</div>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Plan</Label>
              <div className="flex items-center gap-3 mt-1">
                <div className="font-semibold text-slate-900 capitalize">{user?.plan}</div>
                {user?.plan && user.plan !== "free" && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={openPortal}
                    disabled={portalLoading}
                    data-testid="settings-manage-billing"
                    className="h-8 text-xs"
                  >
                    {portalLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                    Manage subscription
                  </Button>
                )}
              </div>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">User ID</Label>
              <div className="font-mono text-xs text-slate-600 mt-1">{user?.user_id}</div>
            </div>
          </div>
        </div>

        {/* 2FA */}
        <div className="vq-card p-6">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-10 h-10 bg-slate-900 rounded flex items-center justify-center shrink-0">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <h2 className="font-display font-bold text-lg text-slate-900">Two-Factor Authentication</h2>
              <p className="text-sm text-slate-600 mt-1">
                Status: <span className="font-semibold text-slate-900">{user?.two_fa_enabled ? "Enabled" : "Disabled"}</span>
              </p>
            </div>
            {user?.two_fa_enabled ? (
              <Button variant="outline" onClick={disable} data-testid="disable-2fa-btn" className="border-slate-300 font-semibold">
                Disable
              </Button>
            ) : !qr && (
              <Button onClick={startSetup} disabled={loading} data-testid="setup-2fa-btn" className="bg-slate-900 hover:bg-[#0055FF] text-white font-semibold">
                Enable 2FA
              </Button>
            )}
          </div>

          {qr && (
            <div className="border-t border-slate-200 pt-6 mt-4">
              <p className="text-sm text-slate-600 mb-4">
                Scan this QR code with your authenticator app (Google Authenticator, Authy, 1Password), then enter the 6-digit code.
              </p>
              <div className="flex flex-col md:flex-row gap-6 items-start">
                <img src={qr.qr_code} alt="2FA QR" className="w-48 h-48 border border-slate-200 rounded" />
                <div className="flex-1 space-y-4">
                  <div>
                    <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Secret (manual entry)</Label>
                    <div className="font-mono text-sm text-slate-900 mt-1 break-all">{qr.secret}</div>
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                      <KeyRound className="w-4 h-4" /> Verification code
                    </Label>
                    <Input
                      data-testid="verify-2fa-code"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      maxLength={6}
                      className="mt-1.5 h-11 font-mono tracking-widest"
                      placeholder="123456"
                    />
                  </div>
                  <Button onClick={verify} disabled={code.length !== 6} data-testid="verify-2fa-btn" className="bg-slate-900 hover:bg-[#0055FF] text-white font-semibold">
                    Verify & enable
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
